import type { AssociatedUser } from '@vendasta/protos/business_center/v1/types';
import { listUsersForBusinessV2$ } from '@vendasta/protos/business_center/v1/user';
import { EMPTY, Observable, defer } from 'rxjs';
import { expand, reduce } from 'rxjs/operators';

const PAGE_SIZE = 100;
const PAGE_TIMEOUT_MS = 30_000;

// Stops a server that always reports another page from looping forever.
const MAX_PAGES = 25;

/**
 * Every user associated with a business, as listed on its User Management page.
 * Prefer this over the deprecated ListUsersForBusiness RPC, which omits some associated users.
 */
export function listAllUsersForBusiness$(accountGroupId: string): Observable<AssociatedUser[]> {
  const fetchPage = (cursor: string) =>
    listUsersForBusinessV2$(
      { accountGroupId, pagingOptions: { pageSize: PAGE_SIZE, cursor } },
      { timeoutMs: PAGE_TIMEOUT_MS },
    );

  return defer(() => {
    let pages = 1;
    return fetchPage('').pipe(
      expand((response) => {
        const cursor = response?.pagingMetadata?.nextCursor;
        if (!response?.pagingMetadata?.hasMore || !cursor) {
          return EMPTY;
        }
        if (pages >= MAX_PAGES) {
          console.warn('[list-all-users-for-business] page limit reached; returning a partial user list', {
            accountGroupId,
            pages,
          });
          return EMPTY;
        }
        pages++;
        return fetchPage(cursor);
      }),
      reduce((users: AssociatedUser[], response) => users.concat(response?.associatedUsers ?? []), []),
    );
  });
}
